import { useEffect, useState } from "react";
import { tryApiGet } from "../api/client";
import { RadioControlPanel } from "./RadioControlPanel";
import type { RadioConfigEntry } from "../api/types";

// Settings needed full radio config for
// both meshcore and meshtastic. Built against the real live response
// (GET /api/radio/config, an array
// with one entry per transport, no pinned response_model on the backend).
//
// Framed in the UI as "Node Configuration" (renamed 2026-09-20, needing to be
// in a NODE CONFIGURATION section IN SETTINGS, after a brief, corrected
// misreading that would
// have moved unrelated alert/automation config out of Alerts instead —
// that move was reverted before it started; this section is the actual
// intended node configuration). The component/prop/API names below stay
// as RadioConfigSection/RadioConfigEntry/GET /api/radio/config — they
// name the real data source (the radio's own reported state), not the
// section's user-facing label, which is the thing that changed.
//
// EDITABLE, NOT READ-ONLY (corrected 2026-09-22: nothing
// surfaced in settings should be read only — these are SETTINGS that the
// operator SETS, and node configuration should NOT be read only).
// An earlier version of this section carried a "Read-only — live device
// state" badge and framed every write as a transmission requiring separate
// approval. That conflated two different things (RADIO_CONTROL_CONTRACT.md
// section 0): TRANSMITTING onto the mesh — creating an emission that would
// not otherwise occur, e.g. a traceroute or a MeshCore advert — which stays
// gated behind the transmit guard and stays closed; and CONTROLLING OUR OWN
// RADIO — reading and writing the configuration of the device at the far
// end of our own existing link, and managing its local node database and
// device actions — which is this product's actual purpose and is not a
// transmission at all. The panel below (RadioControlPanel) renders every
// registered operation from GET /api/radio/control/capabilities, grouped by
// that same rf_class boundary, with local/local_rf_side_effect operations
// fully editable (a single Apply button, disabled until the operator edits
// something on the card, with destructive operations additionally requiring
// the operation id to be typed inline on the card) and rf-class operations
// shown but visibly blocked while the transmit guard is closed —
// never hidden, so the operator can see the feature exists and exactly what
// is blocking it.
export function RadioConfigSection({ defaultOpen = false }: { defaultOpen?: boolean } = {}) {
  const [open, setOpen] = useState(defaultOpen);
  const [entries, setEntries] = useState<RadioConfigEntry[] | null | "unavailable">(null);
  // The /api/radio/config fetch below is keyed only to `open` (the outer
  // Node Configuration section), never to anything inside RadioControlPanel
  // — it needs `entries` in full (all three states, never pre-narrowed) to
  // seed every editable form, enumerate channel cards, and render each
  // transport's own Live snapshot inside that transport's tab, regardless
  // of whether any snapshot block itself is expanded.

  useEffect(() => {
    if (!open || entries !== null) return;
    tryApiGet<RadioConfigEntry[]>("/api/radio/config")
      .then((res) => setEntries(res ?? "unavailable"))
      .catch(() => setEntries("unavailable"));
  }, [open, entries]);

  return (
    <div className="settings-section">
      <div className="settings-section-header" onClick={() => setOpen((o) => !o)}>
        <h3>Node Configuration</h3>
        <span>{open ? "▲" : "▼"}</span>
      </div>
      {open && (
        <div className="settings-section-body">
          <div className="node-detail-section">
            <h3>Radio control</h3>
            <p className="settings-field-desc">
              Read and write each transport's own configuration and local device actions. Each card's Apply button
              stays disabled until you've actually changed something on it, and destructive actions also require
              typing the operation's name to confirm.
            </p>
            <RadioControlPanel entries={entries} />
          </div>
        </div>
      )}
    </div>
  );
}
